import {
	FormRule,
	RuleCondition,
	RuleConditionGroup,
} from "@/dentlab/src/pages/form-builder/types/ruleTypes";
import {
	CustomFormData,
	CustomFormFileType,
	ToothData,
} from "@/dentconnect/src/pages/job-request/types";

/**
 * Evaluates a single rule condition against form data
 */
export const evaluateRuleCondition = (
	condition: RuleCondition,
	formData: CustomFormData
): boolean => {
	const fieldData = formData[condition.fieldId];

	if (!fieldData) {
		// Field not found in form data - treat as empty
		return condition.operator === "is_empty";
	}

	const fieldValue = fieldData.fieldValue;

	// For comparison operators against string array values (e.g. multi-select
	// chips): unwrap a single-element array to its element, and join
	// multi-element arrays into a comma-separated string so rules can match the
	// whole selection as text.
	const fieldValueForComparison = (() => {
		if (
			Array.isArray(fieldValue) &&
			fieldValue.every((v) => typeof v === "string")
		) {
			const arr = fieldValue as string[];
			if (arr.length === 1) return arr[0];
			return arr.join(", ");
		}
		return fieldValue;
	})();

	switch (condition.operator) {
		case "equals":
			return fieldValueForComparison === condition.value;

		case "not_equals":
			return fieldValueForComparison !== condition.value;

		case "includes":
			if (Array.isArray(fieldValue)) {
				// For checkbox fields (array of selected values)
				return fieldValue.includes(condition.value as any);
			}
			if (
				typeof fieldValue === "string" &&
				typeof condition.value === "string"
			) {
				// For text fields
				return fieldValue.includes(condition.value);
			}
			return false;

		case "not_includes":
			if (Array.isArray(fieldValue)) {
				return !fieldValue.includes(condition.value as any);
			}
			if (
				typeof fieldValue === "string" &&
				typeof condition.value === "string"
			) {
				return !fieldValue.includes(condition.value);
			}
			return true;

		case "is_empty":
			if (Array.isArray(fieldValue)) {
				return fieldValue.length === 0;
			}
			if (typeof fieldValue === "string") {
				return fieldValue.trim() === "";
			}
			return fieldValue === null || fieldValue === undefined;

		case "is_not_empty":
			if (Array.isArray(fieldValue)) {
				return fieldValue.length > 0;
			}
			if (typeof fieldValue === "string") {
				return fieldValue.trim() !== "";
			}
			return fieldValue !== null && fieldValue !== undefined;

		case "greater_than":
			if (
				typeof fieldValue === "number" &&
				typeof condition.value === "number"
			) {
				return fieldValue > condition.value;
			}
			if (
				typeof fieldValue === "string" &&
				typeof condition.value === "string"
			) {
				// For date/time comparisons
				return fieldValue > condition.value;
			}
			return false;

		case "less_than":
			if (
				typeof fieldValue === "number" &&
				typeof condition.value === "number"
			) {
				return fieldValue < condition.value;
			}
			if (
				typeof fieldValue === "string" &&
				typeof condition.value === "string"
			) {
				// For date/time comparisons
				return fieldValue < condition.value;
			}
			return false;

		default:
			return false;
	}
};

/**
 * Evaluates a condition group (with AND/OR logic) against form data
 * Supports nested groups
 */
export const evaluateConditionGroup = (
	group: RuleConditionGroup,
	formData: CustomFormData
): boolean => {
	if (group.conditions.length === 0) {
		return true;
	}

	const results = group.conditions.map(
		(condition: RuleCondition | RuleConditionGroup) => {
			// Check if it's a nested group or a single condition
			if ("type" in condition) {
				// It's a nested RuleConditionGroup
				return evaluateConditionGroup(condition, formData);
			} else {
				// It's a RuleCondition
				return evaluateRuleCondition(condition, formData);
			}
		}
	);

	if (group.type === "and") {
		return results.every((result) => result === true);
	} else {
		// OR logic
		return results.some((result) => result === true);
	}
};

/**
 * Finds all matching rules for the given form data
 * Only returns enabled rules whose conditions match
 */
export const getRulesThatAreTrueForSubmittedForm = (
	rules: FormRule[],
	formData: CustomFormData
): FormRule[] => {
	return rules.filter((rule) => {
		// Only evaluate enabled rules
		if (!rule.enabled) {
			return false;
		}

		// Evaluate the rule's condition group
		return evaluateConditionGroup(rule.conditionGroup, formData);
	});
};
